import React, { useContext, useState, useEffect } from "react";
import { Container } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { CartContext } from "../contexts/CartContext";
import { ClientNameContext } from "../contexts/clientNameContext";
import { MaintenanceModeContext } from "../contexts/MaintenanceModeContext";
import { useCartLogic } from "../hooks/useCartLogic";

import CartHeader from "../components/cart/CartHeader";
import CartList from "../components/cart/CartList";
import CartSummary from "../components/cart/CartSummary";
import ShippingForm from "../components/cart/ShippingForm";
import PaymentButtons from "../components/cart/PaymentButtons";

import { API_BASE_URL } from "../Config";
import { handleCheckout, handleError } from "../utils/paymentHandlers";

function CartScreen() {
  const { t, i18n } = useTranslation();
  const maintenanceMode = useContext(MaintenanceModeContext);
  const { cart, setCart, updateQuantityInCart, removeFromCart, updateQuantityAvailable, totalQuantityHeader } = useContext(CartContext);
  const { setClientName } = useContext(ClientNameContext);

  const [isPaymentSuccess, setIsPaymentSuccess] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [clientToken, setClientToken] = useState("");
  const [klarnaReady, setKlarnaReady] = useState(false);
  const [klarnaVisible, setKlarnaVisible] = useState(false);

  const {
    isLoading,
    showAlertEmpty,
    setShowAlertEmpty,
    totalPrice,
    shippingCost,
    totalPriceWithShippingCost,
  } = useCartLogic(cart, setCart);

  const clientName = localStorage.getItem("clientName");
  const clientSurname = localStorage.getItem("clientSurname");

  const [deliveryAddress, setDeliveryAddress] = useState({
    name: clientName,
    lastname: clientSurname,
    address: localStorage.getItem("clientAddress"),
    additional_address: localStorage.getItem("clientAdditionalAddress") || "",
    postal_code: localStorage.getItem("clientPostalCode"),
    city: localStorage.getItem("clientCity"),
    country: localStorage.getItem("clientCountry"),
    number: localStorage.getItem("clientNumber"),
  });

  const handleQuantityChange = (event, productId) => {
    const quantityInCart = Number(event.target.value);
    updateQuantityInCart(productId, quantityInCart);
  };

  const handleRemoveFromCart = (productId) => {
    const item = cart.find((item) => item.product.id === productId);
    if (item) {
      removeFromCart(productId);
      updateQuantityAvailable(productId, item.quantityInCart);
    }
  };

  const handleDeliveryAddressChange = (e) => {
    const { name, value } = e.target;
    setDeliveryAddress((prev) => ({ ...prev, [name]: value }));
  };

  // Carrega Klarna SDK
  useEffect(() => {
    if (window.Klarna && window.Klarna.Payments) {
      setKlarnaReady(true);
      return;
    }

    const script = document.createElement("script");
    script.src = "https://x.klarnacdn.net/kp/lib/v1/api.js";
    script.async = true;
    script.onload = () => setKlarnaReady(true);
    script.onerror = () => console.error("❌ Falha ao carregar Klarna SDK");
    document.body.appendChild(script);
  }, []);

  // Cria sessão Klarna e renderiza widget inline
  const handleKlarnaPayment = async () => {
    try {
      const clientId = localStorage.getItem("clientId");

      const res = await fetch(`${API_BASE_URL}/klarna/session/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ cart, totalPriceWithShippingCost, clientId }),
      });

      const data = await res.json();

      if (data.order_id) {
        const popupUrl = `https://demo.lmbrasil.de/klarna/checkout/${data.order_id}`;
        const popup = window.open(
          popupUrl,
          "KlarnaPopup",
          "width=500,height=700,scrollbars=yes,resizable=yes"
        );

        if (!popup) {
          window.location.href = popupUrl;
        }
      }
    } catch (err) {
      console.error("❌ Erro Klarna:", err);
    }
  };

  // Renderiza Klarna widget
  useEffect(() => {
    if (
      klarnaReady &&
      clientToken &&
      window.Klarna &&
      window.Klarna.Payments &&
      klarnaVisible
    ) {
      window.Klarna.Payments.init({ client_token: clientToken });

      window.Klarna.Payments.load(
        {
          container: "#klarna-container",
          payment_method_category: "pay_later",
          on_rendered: () => console.log("✅ Klarna renderizado"),
          on_complete: (res) =>
            console.log("✅ Autorizado:", res.authorization_token),
          on_error: (err) => {
            console.warn("⚠️ Klarna pay_later falhou, tentando pay_now");
            window.Klarna.Payments.load({
              container: "#klarna-container",
              payment_method_category: "pay_now",
            });
          },
        },
        (res) => {
          if (!res.show_form) {
            console.warn("⚠️ Klarna não mostrou formulário:", res);
          }
        }
      );
    }
  }, [klarnaReady, clientToken, klarnaVisible]);

  if (maintenanceMode) {
    return <div className="text-center pt-5">{t("website_maintenance")}</div>;
  }

  return (
    <Container className="pt-4">
      <CartHeader
        isLoading={isLoading}
        showAlert={showAlert}
        setShowAlert={setShowAlert}
        showAlertEmpty={showAlertEmpty}
        setShowAlertEmpty={setShowAlertEmpty}
      />

      {cart.length > 0 && (
        <>
          <CartList
            cart={cart}
            handleQuantityChange={handleQuantityChange}
            handleRemoveFromCart={handleRemoveFromCart}
          />

          <CartSummary
            totalPrice={totalPrice}
            shippingCost={shippingCost}
            totalPriceWithShippingCost={totalPriceWithShippingCost}
            totalQuantityHeader={totalQuantityHeader}
          />

          {clientName ? (
            <>
              <ShippingForm
                clientName={clientName}
                clientSurname={clientSurname}
                deliveryAddress={deliveryAddress}
                handleDeliveryAddressChange={handleDeliveryAddressChange}
              />

              <PaymentButtons
                totalPriceWithShippingCost={totalPriceWithShippingCost}
                handleCheckout={() =>
                  handleCheckout({
                    cart,
                    deliveryAddress,
                    clientName,
                    clientSurname,
                    setCart,
                    setClientName,
                    setShowAlert,
                    setIsPaymentSuccess,
                    i18n,
                  })
                }
                handleKlarnaPayment={handleKlarnaPayment}
                isPaymentSuccess={isPaymentSuccess}
                handleError={handleError}
              />

              {klarnaVisible && (
                <div className="mt-4">
                  <h5>{t("klarna_payment")}</h5>
                  <div
                    id="klarna-container"
                    style={{
                      minHeight: "300px",
                      border: "1px solid #ccc",
                      padding: "1rem",
                      background: "#fff",
                    }}
                  ></div>
                </div>
              )}
            </>
          ) : (
            <div className="text-center pt-4">
              <a href="/login" className="btn btn-primary">
                {t("login")}
              </a>
            </div>
          )}
        </>
      )}
    </Container>
  );
}

export default CartScreen;
